import React from 'react';
import { useTranslation } from 'react-i18next';
import { Star, MessageSquare, Utensils, CheckCircle, AlertCircle, XCircle, Clock } from 'lucide-react';

const RestaurantOverviewCard = ({ restaurantData, isLoading }) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="bg-gradient-to-r from-blue-400 to-blue-500 rounded-lg shadow-sm p-6 text-white">
        <div className="animate-pulse">
          <div className="h-8 bg-blue-400 rounded w-48 mb-2"></div>
          <div className="h-4 bg-blue-400 rounded w-32 mb-4"></div>
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="text-center">
                <div className="h-6 bg-blue-400 rounded w-16 mx-auto mb-1"></div>
                <div className="h-3 bg-blue-400 rounded w-20 mx-auto"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!restaurantData) {
    return null;
  }

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'inactive': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'inactive': return <XCircle className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  return (
    <div className="bg-gradient-to-r from-blue-400 to-blue-500 rounded-lg shadow-sm p-6 text-white mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold">
            {restaurantData.restaurantName || t('dashboard.restaurantDashboard')}
          </h2>
          <p className="text-blue-100 mt-1">
            {new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
        <div className="text-right">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(restaurantData.restaurantStatus)}`}>
            {getStatusIcon(restaurantData.restaurantStatus)}
            <span className="ml-2 rtl:mr-2 rtl:ml-0">{restaurantData.restaurantStatus || t('dashboard.statusUnknown')}</span>
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="text-center">
          <div className="flex items-center justify-center mb-2">
            <Star className="w-6 h-6 text-yellow-300 mr-3 rtl:ml-3 rtl:mr-0" />
            <p className="text-2xl font-bold text-white">
              {restaurantData.restaurantRating ? restaurantData.restaurantRating.toFixed(1) : t('dashboard.notAvailable')}
            </p>
          </div>
          <p className="text-blue-100 text-sm">{t('dashboard.rating')}</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center mb-2">
            <MessageSquare className="w-6 h-6 text-blue-200 mr-3 rtl:ml-3 rtl:mr-0" />
            <p className="text-2xl font-bold text-white">
              {restaurantData.totalReviews || 0}
            </p>
          </div>
          <p className="text-blue-100 text-sm">{t('dashboard.totalReviews')}</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center mb-2">
            <Utensils className="w-6 h-6 text-green-300 mr-3 rtl:ml-3 rtl:mr-0" />
            <p className="text-2xl font-bold text-white">
              {restaurantData.activeMeals || 0}
            </p>
          </div>
          <p className="text-blue-100 text-sm">{t('dashboard.activeMeals')}</p>
        </div>
      </div>
    </div>
  );
};

export default RestaurantOverviewCard;
